import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BsInfoCircle, BsCheckCircleFill, BsClockHistory, BsCircle } from 'react-icons/bs';
import { useDashboard } from '../context/DashboardContext';
import { fetchChecklistStats } from '../../../features/checks/checkActions';

export default function TodaysChecklist() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedDate, refreshKey } = useDashboard();
  const { data: stats, loading: statsLoading, error: statsError } = useSelector( (state) => state.checks.checklistStats);

  // ============================================================
  // FETCH STATS WHEN DATE CHANGES
  // ============================================================

  useEffect(() => {
    if (!selectedDate) return;

    const request = dispatch(fetchChecklistStats(selectedDate));
    return () => request.abort();
  }, [dispatch, selectedDate, refreshKey]);

  // ============================================================
  // CHECKLISTS
  // ============================================================

  const checks = useMemo(() => {
    const checklistStats = stats?.checklists || [];

    return checklistStats.map((checklist) => {
      const totalTasks = Number(checklist.totalTasks || 0);
      const completedTasks = Number(checklist.completedTasks || 0);
      const actionRequired = Number(checklist.actionRequired || 0);

      let status = checklist.frequency === 'AS_REQUIRED' ? 'As Required' : 'Pending-Gray';

      if (
        totalTasks > 0 &&
        completedTasks === totalTasks
      ) {
        status = 'Completed';
      } else if (actionRequired > 0 && checklist.frequency !== 'AS_REQUIRED') {
        status = 'Pending-Amber';
      }

      return {
        id: checklist.id,
        name: checklist.name,
        slug: checklist.slug,
        frequency: checklist.frequency,

        totalTasks,
        completedTasks,
        actionRequired,

        status,

        assignedName: 'Not tracked',
        assignedRole: '—'
      };
    });
  }, [stats]);

  // ============================================================
  // DATE
  // ============================================================

  const formattedDate = useMemo(() => {
    if (!selectedDate) return '';

    const date = new Date(`${selectedDate}T00:00:00`);

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }, [selectedDate]);

  // ============================================================
  // NAVIGATION
  // ============================================================

  const handleCheckClick = (item) => {
    if (item.slug) {
      navigate(`/checks/${item.slug}?date=${selectedDate}`);
      return;
    }

    navigate('/checks');
  };

  const handleViewAll = () => {
    navigate('/checks');
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="w-full bg-white border border-slate-100 rounded-3xl p-6 shadow-sm font-sans text-slate-800">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">

        <div className="flex items-center gap-1.5">

          <h2 className="text-lg font-bold text-slate-900">
            Today's Checklist
          </h2>

          <BsInfoCircle
            className="text-slate-400 text-xs cursor-pointer hover:text-slate-600"
          />

        </div>

        <button
          onClick={handleViewAll}
          className="text-[#3a31d8] text-xs font-bold hover:underline"
        >
          View All Checks
        </button>

      </div>

      {/* DATE */}
      <div className="flex items-center gap-3 mb-6">

        <span className="text-base font-bold text-slate-900">
          {formattedDate}
        </span>

        <span className="bg-[#eeecff] text-[#4d3df5] text-[10px] font-bold px-2 py-0.5 rounded-md">
          {checks.filter(check => check.frequency === 'DAILY').length} daily ·{' '}
          {checks.filter(check => check.frequency === 'AS_REQUIRED').length} as required
        </span>

      </div>

      {/* TABLE HEADERS */}
      <div className="grid grid-cols-12 text-[11px] font-bold text-slate-400 tracking-wider uppercase pb-3 border-b border-slate-100">

        <div className="col-span-5">
          Check
        </div>

        <div className="col-span-4">
          Assigned To
        </div>

        <div className="col-span-3 text-right md:text-left md:pl-4">
          Status
        </div>

      </div>

      {/* LOADING */}
      {statsLoading && (
        <div className="py-10 text-center text-sm text-slate-400">
          Loading today's checks...
        </div>
      )}

      {/* ERROR */}
      {!statsLoading && statsError && (
        <div className="py-10 text-center text-sm text-red-500">
          {statsError}
        </div>
      )}

      {/* EMPTY */}
      {!statsLoading &&
        !statsError &&
        checks.length === 0 && (
          <div className="py-10 text-center text-sm text-slate-400">
            No checks scheduled for this date.
          </div>
        )}

      {/* CHECKLIST ROWS */}
      {!statsLoading &&
        !statsError &&
        checks.length > 0 && (

          <div className="divide-y divide-slate-50 mb-4">

            {checks.map((item) => (

              <button
                key={item.id}
                type="button"
                onClick={() => handleCheckClick(item)}
                className="
                  grid
                  grid-cols-12
                  items-center
                  w-full
                  py-3.5
                  text-sm
                  text-left
                  transition
                  hover:bg-slate-50
                  hover:px-2
                  rounded-lg
                  cursor-pointer
                "
              >

                {/* CHECK NAME */}
                <div className="col-span-5 font-semibold text-slate-800 pr-2">
                  {item.name}
                </div>

                {/* ASSIGNED TO */}
                <div className="col-span-4">

                  <div className="font-semibold text-slate-700">
                    {item.assignedName}
                  </div>

                  <div className="text-[11px] text-slate-400 font-medium">
                    {item.assignedRole}
                  </div>

                </div>

                {/* STATUS */}
                <div className="col-span-3 flex flex-col items-end md:items-start md:pl-4">

                  {/* COMPLETED */}
                  {item.status === 'Completed' && (
                    <>
                      <div className="flex items-center gap-1.5 text-green-600 font-bold text-[11px]">

                        <BsCheckCircleFill className="text-xs" />

                        <span>
                          Completed
                        </span>

                      </div>

                      <span className="text-[11px] text-slate-400 font-medium mt-0.5 mr-0.5 md:ml-4">
                        {item.completedTasks}/{item.totalTasks}
                      </span>
                    </>
                  )}

                  {item.status === 'As Required' && (
                    <div className="flex items-center gap-1.5 text-blue-600 font-bold text-[11px]">
                      <BsInfoCircle className="text-xs" />
                      <span>As Required</span>
                    </div>
                  )}

                  {/* PENDING WITH ACTION */}
                  {item.status === 'Pending-Amber' && (
                    <>
                      <div className="flex items-center gap-1.5 text-amber-500 font-bold text-[11px]">

                        <BsClockHistory className="text-xs" />

                        <span>
                          Pending
                        </span>

                      </div>

                      <span className="text-[11px] text-slate-400 font-medium mt-0.5 mr-2 md:ml-4">
                        {item.completedTasks}/{item.totalTasks}
                      </span>
                    </>
                  )}

                  {/* PENDING */}
                  {item.status === 'Pending-Gray' && (
                    <>
                      <div className="flex items-center gap-1.5 text-slate-400 font-bold text-[11px]">

                        <BsCircle className="text-xs stroke-[0.5]" />

                        <span>
                          Pending
                        </span>

                      </div>

                      <span className="text-[11px] text-slate-400 font-medium mt-0.5 mr-2 md:ml-4">
                        {item.completedTasks}/{item.totalTasks}
                      </span>
                    </>
                  )}

                </div>

              </button>

            ))}

          </div>
        )}

      {/* FOOTER */}
      <div className="bg-[#edf9f1] rounded-xl p-3 flex items-center justify-between text-xs font-bold text-green-700">

        <div className="flex items-center gap-2">

          <span className="font-semibold">
            Overall Completion
          </span>

        </div>

        <div className="flex items-center gap-4">

          <div className="flex items-center gap-1.5">

            <BsCheckCircleFill className="text-green-600 text-sm" />

            <span>
              {stats?.completedTasks ?? 0} / {stats?.totalTasks ?? 0}{' '}

              <span className="font-normal text-green-600/80">
                Completed ({stats?.completionPercentage ?? 0}%)
              </span>

            </span>

          </div>

          <button
            type="button"
            onClick={() => navigate('/checks')}
            className="text-[#3a31d8] font-bold hover:underline"
          >
            View
          </button>

        </div>

      </div>

    </div>
  );
}
